import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
  Icon,
  SocialMediaIcon,
  type SocialMediaIconProps,
} from '@kontentino/ui';
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { faHeart } from '@fortawesome/pro-regular-svg-icons/faHeart';
import { faComment } from '@fortawesome/pro-regular-svg-icons/faComment';
import PostTypeIcon from 'app/modules/posts/components/PostTypeIcon';
import { getContentPostPreviewImageUrl } from 'app/modules/analytics/modules/content/utils/getContentPostPreviewImageUrl';
import { getContentPostRowPostType } from 'app/modules/analytics/modules/content/utils/contentPostType';
import {
  formatCompactMetric,
  getContentPostTotalEngagement,
} from 'app/modules/analytics/modules/content/utils/contentPostEngagement';
import type {
  BestContentPost,
  ContentPost,
  OverviewResponse,
} from 'api/new-analytics/types';

interface PostItemProps {
  post: BestContentPost;
  isBest: boolean;
}

function PostItem({ post, isBest }: PostItemProps) {
  const { t } = useTranslation();
  const asContentPost = post as unknown as ContentPost;
  const m = post.metrics;
  const engagementTotal = getContentPostTotalEngagement(asContentPost);
  const postType = getContentPostRowPostType(asContentPost);
  const mediaUrl = getContentPostPreviewImageUrl(asContentPost);
  const reach = m.reach ?? m.views;
  const likes = m.likes ?? m.reactions;
  const { comments } = m;

  return (
    <div
      className={clsx(
        'tw-flex tw-min-w-0 tw-flex-col tw-overflow-hidden tw-rounded-lg tw-bg-white',
        {
          'tw-border-2 tw-border-warning-100': isBest,
          'tw-border tw-border-grayscale-20': !isBest,
        },
      )}
    >
      <div className="analytics-report-top-post-card-media tw-relative tw-h-[89px] tw-w-full tw-shrink-0 tw-overflow-hidden tw-bg-grayscale-10">
        {mediaUrl ? (
          <img
            alt=""
            src={mediaUrl}
            className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover"
          />
        ) : (
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
            <PostTypeIcon type={postType} className="tw-text-grayscale-50" />
          </div>
        )}
        <div className="tw-absolute tw-right-1 tw-top-1 tw-flex tw-flex-col tw-items-end tw-gap-1">
          {post.is_promoted ? (
            <div className="tw-rounded tw-bg-warning-100 tw-px-1 tw-py-1 tw-text-[9px] tw-font-medium tw-leading-[12px] tw-text-grayscale-180">
              {t('promoted', { defaultValue: 'Promoted' })}
            </div>
          ) : null}
          {mediaUrl ? (
            <div className="tw-flex tw-h-6 tw-w-7 tw-items-center tw-justify-center tw-rounded tw-bg-grayscale-5">
              <PostTypeIcon type={postType} size="sm" />
            </div>
          ) : null}
        </div>
      </div>

      <div className="tw-flex tw-flex-col tw-gap-3 tw-p-2">
        <p className="tw-line-clamp-2 tw-min-h-[32px] tw-text-xs tw-leading-4 tw-text-grayscale-180">
          {post.preview?.text?.trim() || post.content?.trim() || (
            <span className="tw-italic tw-text-grayscale-100">
              {t('analyticsContent.noCaptionPlaceholder')}
            </span>
          )}
        </p>

        <div
          className={clsx(
            'tw-relative tw-flex tw-h-16 tw-items-center tw-rounded-lg tw-border tw-p-px',
            {
              'tw-border-warning-20 tw-bg-warning-10': isBest,
              'tw-border-grayscale-10 tw-bg-grayscale-5': !isBest,
            },
          )}
        >
          <div className="tw-flex tw-h-full tw-w-[93px] tw-shrink-0 tw-flex-col tw-items-center tw-justify-center">
            <span className="tw-text-xl tw-font-semibold tw-text-grayscale-180">
              {formatCompactMetric(engagementTotal)}
            </span>
            <span className="tw-text-[10px] tw-font-medium tw-leading-[14px] tw-text-grayscale-180">
              {t('analyticsContent.metrics.engagement')}
            </span>
          </div>

          <div className="tw-h-full tw-w-px tw-shrink-0 tw-bg-grayscale-10" />

          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-px-3">
            {reach != null && (
              <div className="tw-flex tw-items-center tw-gap-1">
                <Icon
                  icon={faEye}
                  size={12}
                  className="tw-text-grayscale-100"
                />
                <span className="tw-whitespace-nowrap tw-text-[10px] tw-font-medium tw-leading-[14px] tw-text-grayscale-180">
                  {formatCompactMetric(reach)}
                </span>
              </div>
            )}
            {likes != null && (
              <div className="tw-flex tw-items-center tw-gap-1">
                <Icon
                  icon={faHeart}
                  size={12}
                  className="tw-text-grayscale-100"
                />
                <span className="tw-whitespace-nowrap tw-text-[10px] tw-font-medium tw-leading-[14px] tw-text-grayscale-180">
                  {formatCompactMetric(likes)}
                </span>
              </div>
            )}
            {comments != null && (
              <div className="tw-flex tw-items-center tw-gap-1">
                <Icon
                  icon={faComment}
                  size={12}
                  className="tw-text-grayscale-100"
                />
                <span className="tw-whitespace-nowrap tw-text-[10px] tw-font-medium tw-leading-[14px] tw-text-grayscale-180">
                  {formatCompactMetric(comments)}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-gap-2">
          <SocialMediaIcon
            size={16}
            type={post.platform as SocialMediaIconProps['type']}
          />
          <span className="tw-font-normal tw-text-[10px] tw-leading-[14px] tw-text-grayscale-100">
            {dayjs(post.published_at).format('MMM D')}
          </span>
        </div>
      </div>
    </div>
  );
}

interface Props {
  data: OverviewResponse;
  isGroupSelected: boolean;
}

const ReportPreviewOverviewBestContent = ({ data, isGroupSelected }: Props) => {
  const { t } = useTranslation();
  // TODO(overview-pdf-be): Posty musia prísť z preview OverviewResponse.best_content (BE),
  // nie z content report sekcie growth_pulse workaroundu.
  const maxPosts = isGroupSelected ? 3 : 4;
  const posts = useMemo(
    () => (data.best_content ?? []).slice(0, maxPosts),
    [data.best_content, maxPosts],
  );

  return (
    <div
      className="tw-relative tw-flex tw-flex-col tw-gap-3 tw-overflow-hidden tw-rounded-xl tw-border tw-border-grayscale-10 tw-bg-white tw-p-3 tw-shadow-sm"
      data-cy="analytics-overview-export_best-content-card"
    >
      <span className="tw-text-base tw-font-semibold tw-text-grayscale-180">
        {t('bestContent', { defaultValue: 'Best content' })}
      </span>

      {posts.length === 0 ? (
        <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-py-8">
          <span className="tw-text-sm tw-text-grayscale-100">
            {t('noDataAvailable', { defaultValue: 'No data available' })}
          </span>
        </div>
      ) : (
        <div
          className={clsx(
            'analytics-report-overview-posts-grid tw-grid tw-gap-3',
            {
              'tw-grid-cols-3': isGroupSelected,
              'tw-grid-cols-4': !isGroupSelected,
            },
          )}
        >
          {posts.map((post, index) => (
            <PostItem
              key={post.post_id ?? post.entity_id ?? index}
              post={post}
              isBest={index === 0}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportPreviewOverviewBestContent;
